import React, { useCallback } from 'react';
import { Trans } from '@wix/cli-i18n';
import { matcher, match, isOfVariant, variantList, isType } from 'variant';
import { openBrowser } from '@wix/user-browser';
import type { Site } from '@wix/site-list-client';
import { Alert, Link, Text, Newline } from '@wix/cli-ui-kit';
import { PlatformType } from '@wix/cli-app-definitions';
import { URL } from 'node:url';
import { useProjectModel } from '../../model.js';
import { useSite } from '../../site/useSite.js';
import { useArmTag } from '../../context/armTag.js';
import type { DevCenterComponent } from '../../dev-center/components/dev-center-component.js';
import type { DashboardExtension } from './useExtensions.js';
import { useSiteOverrideId } from '../../context/siteOverride.js';
import { useDevEnvironment } from '../DevCommand/DevEnvironment.js';
import { useBlocksCLIDevServerProvider } from '../DevCommand/BlocksCLIServerProvider.js';
import { useExtensionData } from '../../context/extensionData.js';

/*
This function is a workaround to open the new CSM v2 experience to external users before the experiment is open to external users.

In the *editor and viewer* we are using elgar to redirect the user to the editor with the overrides in his cookie - since there is no support for query params overrides in certain flows.
*/
export const redirectWithCSMv2FTs = (
  url: URL,
  platformType: PlatformType
): URL => {
  const elgarBaseURL = isType(platformType, PlatformType.Site)
    ? `${url.origin}/_api`
    : 'https://apps.wix.com';
  const elgarRedirectUrl = new URL(`${elgarBaseURL}/elgar-server/redirect`);

  const petriOvr = [
    // TODO: Remove once Dev Versions effort is done https://wix.atlassian.net/browse/FEDINF-13336
    'specs.UseAppsRuntimeCache',
    'specs.ApplyQueryParamsOverrides',
    'specs.ExcludeSiteFromSsrCache',
    // TODO: Remove once Blocks on CLI effort is done
    'specs.thunderbolt.passWidgetBundleUrls',
    'specs.thunderbolt.passBlocksWidgetBundleUrls',
    'specs.blocks-client.useBundledWidgetCode',
    'specs.blocks-widget-services.usePanelExtension',
  ];

  for (const spec of petriOvr) {
    elgarRedirectUrl.searchParams.append('experiments', `${spec}#true`);
  }
  elgarRedirectUrl.searchParams.append('redirectToUrl', url.toString());
  elgarRedirectUrl.searchParams.append('staticsVersions', '{}');

  return elgarRedirectUrl;
};

function wrapWithRedirectUrl({
  url,
  clientDevServerUrl,
}: {
  url: string;
  clientDevServerUrl: string;
}) {
  const redirectUrl = new URL('/redirect', clientDevServerUrl);
  redirectUrl.searchParams.append('url', url);

  return redirectUrl.href;
}

export function useResolvePlatformUrl() {
  const { model } = useProjectModel();
  const { site } = useSite();
  const { armTag } = useArmTag();
  const { siteOverrideId } = useSiteOverrideId();

  return useCallback(
    (platformType: PlatformType, openAddPanel?: boolean) => {
      return resolvePlatformUrl(platformType, {
        openAddPanel,
        site,
        appId: model.config.appId,
        tag: armTag,
        siteOverrideId,
      });
    },
    [site, model.config.appId, armTag, siteOverrideId]
  );
}

export function useResolvePlatformDevUrl() {
  const resolvePlatformUrl = useResolvePlatformUrl();
  const { https, clientDevServerUrl } = useDevEnvironment();

  return useCallback(
    (platformType: PlatformType, openAddPanel?: boolean) => {
      let url = resolvePlatformUrl(platformType, openAddPanel);

      if (https) {
        url = wrapWithRedirectUrl({ url, clientDevServerUrl });
      }

      return url;
    },
    [resolvePlatformUrl, https, clientDevServerUrl]
  );
}

function resolvePlatformUrl(
  platformType: PlatformType,
  options: {
    site: Site;
    appId: string;
    tag: string;
    openAddPanel?: boolean;
    siteOverrideId?: string;
  }
) {
  const { site, tag, siteOverrideId, appId, openAddPanel } = options;

  const url = match(platformType, {
    Dashboard: () => new URL(`dashboard/${site.id}`, 'https://manage.wix.com/'),
    Editor: () => {
      const editorUrl = new URL(`https://wix.com/editor/${site.id}`);

      if (openAddPanel) {
        editorUrl.searchParams.append('deeplink', `addpanel!${appId}`);
      }

      return editorUrl;
    },
    Site: () => {
      const siteUrl = new URL(site.viewUrl!);
      if (siteOverrideId) {
        siteUrl.searchParams.append('site-override', siteOverrideId);
      }
      return siteUrl;
    },
    Blocks: () => new URL(`https://wix.com/editor/${site.id}`),
  });

  url.searchParams.append('apps-override', tag);

  return isOfVariant(
    platformType,
    variantList([PlatformType.Editor, PlatformType.Site])
  )
    ? redirectWithCSMv2FTs(url, platformType).toString()
    : url.toString();
}

export function useResolveDashboardExtensionUrl() {
  const { model } = useProjectModel();
  const { site } = useSite();
  const { armTag } = useArmTag();
  const { siteOverrideId } = useSiteOverrideId();
  const { getDashboardExtensionsRoute } = useExtensionData();

  return useCallback(
    (extension: DevCenterComponent<DashboardExtension>) => {
      return resolveDashboardExtensionUrl(extension, {
        site,
        appId: model.config.appId,
        tag: armTag,
        siteOverrideId,
        route: getDashboardExtensionsRoute(extension),
      });
    },
    [
      site,
      model.config.appId,
      armTag,
      siteOverrideId,
      getDashboardExtensionsRoute,
    ]
  );
}

export function useResolveDashboardExtensionDevUrl() {
  const resolveDashboardExtensionUrl = useResolveDashboardExtensionUrl();
  const { https, clientDevServerUrl } = useDevEnvironment();

  return useCallback(
    (extension: DevCenterComponent<DashboardExtension>) => {
      let url = resolveDashboardExtensionUrl(extension);

      if (https) {
        url = wrapWithRedirectUrl({ url, clientDevServerUrl });
      }

      return url;
    },
    [resolveDashboardExtensionUrl, https, clientDevServerUrl]
  );
}

function getBlocksUrls(appId: string, localDevPort: number) {
  return {
    first: `https://blocks.wix.com/wix-blocks-new-app-creator?editorType=RESPONSIVE&appDefinitionId=${appId}&localDevPort=${localDevPort}&http_referrer=CLI`,
    other: `https://blocks.wix.com/_api/wix-blocks-service/web/v1/apps/${appId}/open?localDevPort=${localDevPort}&http_referrer=CLI`,
  };
}

export function useResolveBlocksEditorURL() {
  const { model } = useProjectModel();
  const { enabled: cliBlocksServerEnabled, port: cliBlocksServerPort } =
    useBlocksCLIDevServerProvider();
  const { https, clientDevServerUrl } = useDevEnvironment();

  return useCallback(() => {
    if (!cliBlocksServerEnabled || !cliBlocksServerPort) {
      throw new Error('Blocks CLI Server is not running');
    }

    const appId = model.config.appId;
    const blocksURLS = getBlocksUrls(appId, cliBlocksServerPort);

    let url = !model.config.blocks?.uiRevision
      ? blocksURLS.first
      : blocksURLS.other;

    if (https) {
      url = wrapWithRedirectUrl({ url, clientDevServerUrl });
    }

    return url;
  }, [
    model.config.appId,
    model.config.blocks?.uiRevision,
    clientDevServerUrl,
    cliBlocksServerPort,
    https,
    cliBlocksServerEnabled,
  ]);
}

function resolveDashboardExtensionUrl(
  extension: DevCenterComponent<DashboardExtension>,
  options: {
    route: string;
    site: Site;
    appId: string;
    tag: string;
    siteOverrideId?: string;
  }
) {
  const { appId, site, tag, route } = options;

  const url = matcher(extension)
    .with({
      DashboardPage: () =>
        new URL(
          `dashboard/${site.id}/app/${appId}${route}`,
          'https://manage.wix.com/'
        ),
    })
    .when(
      ['DashboardMenuPlugin', 'DashboardPlugin', 'DashboardModal'],
      () => new URL(`dashboard/${site.id}${route}`, 'https://manage.wix.com/')
    )
    .complete();

  url.searchParams.append('apps-override', tag);

  return url.toString();
}

export const useOpenPage = () => {
  const { logger } = useDevEnvironment();

  return useCallback(
    async ({ url, title }: { url: string; title: string }) => {
      try {
        await openBrowser(url);
      } catch {
        logger.error(
          <Alert type="error">
            <Trans
              i18nKey="general.open_page_error"
              components={[<Text bold />]}
              values={{ title }}
            />
            <Newline />
            <Link url={url} />
          </Alert>
        );
      }
    },
    [logger]
  );
};
